import React, { useEffect, useRef, useState } from "react";
import {
  CheckIcon,
  ChevronRightIcon,
  FolderIcon,
  FolderPlusIcon,
  LockClosedIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  ancestorPath,
  isDescendant,
} from "./projectFolderTreeUtils";

function Highlight({ text, q }) {
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="rounded bg-blue-100 px-0.5 text-gray-900">{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}

function FolderRow({
  item,
  scopeTree,
  selected,
  isFocus,
  inSpine,
  expanded,
  editing,
  draft,
  setDraft,
  commitEdit,
  cancelEdit,
  onToggleExpand,
  onRowClick,
  onToggleSelect,
  onCreateChild,
  onStartRename,
  onDelete,
  drag,
  setDrag,
  onMove,
  matched,
  searching,
  multiSelect,
  allowManage,
  t,
}) {
  const { node, depth, hasKids } = item;
  const inputRef = useRef(null);
  const ancestors = ancestorPath(scopeTree, node.id) || [];

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const childCount = node.children?.length ?? 0;
  const isDropInside =
    drag.over?.id === node.id && drag.over?.pos === "inside";
  const isDropBefore =
    drag.over?.id === node.id && drag.over?.pos === "before";
  const isDropAfter =
    drag.over?.id === node.id && drag.over?.pos === "after";
  const isDragging = drag.id === node.id;
  const indent = depth * 22;

  const handleDragOver = (e) => {
    if (!drag.id || !allowManage) return;
    e.preventDefault();
    if (
      drag.id === node.id ||
      isDescendant(scopeTree, node.id, drag.id)
    ) {
      setDrag((d) => ({ ...d, over: null }));
      return;
    }
    const r = e.currentTarget.getBoundingClientRect();
    const y = (e.clientY - r.top) / r.height;
    let pos = "inside";
    if (y < 0.28) pos = "before";
    else if (y > 0.72) pos = "after";
    setDrag((d) =>
      d.over?.id === node.id && d.over?.pos === pos
        ? d
        : { ...d, over: { id: node.id, pos } }
    );
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (!drag.id || drag.id === node.id || !allowManage) return;
    if (isDescendant(scopeTree, node.id, drag.id)) return;
    onMove(drag.id, node.id, drag.over?.pos ?? "inside");
    setDrag({ id: null, over: null });
  };

  return (
    <div
      role="option"
      aria-selected={selected}
      className={[
        "relative flex cursor-pointer select-none items-center gap-1 rounded-lg py-0 pr-2 text-[13.5px] text-gray-700 transition-colors",
        "min-h-[38px]",
        selected ? "bg-blue-50 text-gray-900" : "hover:bg-gray-50",
        isFocus && !selected ? "ring-1 ring-inset ring-blue-200" : "",
        isDragging ? "opacity-40" : "",
        isDropInside ? "bg-blue-50 ring-2 ring-inset ring-blue-500" : "",
        searching && !matched ? "opacity-40" : "",
      ].join(" ")}
      draggable={allowManage && !editing}
      onDragStart={(e) => {
        if (!allowManage) return;
        e.stopPropagation();
        setDrag({ id: node.id, over: null });
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragEnd={() => setDrag({ id: null, over: null })}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={(e) => !editing && onRowClick(node, e)}
    >
      {selected && (
        <span className="absolute bottom-[18%] left-0.5 top-[18%] w-0.5 rounded-full bg-blue-600" />
      )}
      {isDropBefore && (
        <div
          className="absolute right-2.5 top-0 z-[2] h-0.5 rounded bg-blue-600 before:absolute before:-left-1 before:-top-0.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-blue-600"
          style={{ left: indent + 30 }}
        />
      )}
      {isDropAfter && (
        <div
          className="absolute bottom-0 right-2.5 z-[2] h-0.5 rounded bg-blue-600 before:absolute before:-left-1 before:-top-0.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-blue-600"
          style={{ left: indent + 30 }}
        />
      )}

      <div
        className="pointer-events-none absolute bottom-0 left-0 top-0"
        style={{ width: indent + 8 }}
      >
        {ancestors.map((aid, i) => (
          <span
            key={aid}
            className={[
              "absolute bottom-0 top-0 w-px rounded-sm bg-gray-200 transition-all",
              inSpine.has(aid)
                ? "w-0.5 bg-blue-600 shadow-[0_0_6px_-1px_rgba(37,99,235,0.5)]"
                : "",
            ].join(" ")}
            style={{ left: i * 22 + 17 }}
          />
        ))}
      </div>

      <button
        type="button"
        className="z-[1] flex h-5 w-5 shrink-0 items-center justify-center border-0 bg-transparent p-0 text-gray-400 hover:text-gray-700"
        style={{ marginLeft: indent }}
        onClick={(e) => {
          e.stopPropagation();
          if (hasKids) onToggleExpand(node.id);
        }}
        tabIndex={-1}
        aria-label={hasKids ? (expanded ? t("folder_selector.collapse") : t("folder_selector.expand")) : undefined}
      >
        {hasKids ? (
          <ChevronRightIcon
            className={[
              "h-3.5 w-3.5 transition-transform",
              expanded ? "rotate-90" : "",
            ].join(" ")}
          />
        ) : (
          <span className="h-1 w-1 rounded-full bg-gray-300" />
        )}
      </button>

      {multiSelect && (
        <button
          type="button"
          className={[
            "grid h-[18px] w-[18px] shrink-0 place-items-center rounded-md border-[1.5px] transition-opacity",
            selected
              ? "border-blue-600 bg-blue-600 text-white opacity-100"
              : "border-gray-200 bg-white opacity-0 group-hover/fs-row:opacity-100 [.is-selected_&]:opacity-100",
          ].join(" ")}
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(node.id);
          }}
          aria-label={selected ? t("folder_selector.deselect") : t("folder_selector.select")}
        >
          {selected && <CheckIcon className="h-3 w-3" />}
        </button>
      )}

      <span
        className={[
          "grid h-[22px] w-[22px] shrink-0 place-items-center",
          selected ? "text-blue-600" : "text-blue-500/80",
        ].join(" ")}
        aria-hidden
      >
        <FolderIcon className="h-4 w-4" />
      </span>

      {node.access === "admins" ? (
        <LockClosedIcon className="h-3.5 w-3.5 shrink-0 text-amber-600" title={t("document_archive.access_admins")} />
      ) : null}

      {editing ? (
        <input
          ref={inputRef}
          className="fs-inline-input min-w-0 flex-1 rounded-md border border-blue-500 bg-white px-2 py-0.5 text-[13.5px] text-gray-900 outline-none ring-4 ring-blue-500/15"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitEdit();
            else if (e.key === "Escape") cancelEdit();
          }}
          onBlur={commitEdit}
        />
      ) : (
        <span className="min-w-0 flex-1 truncate font-medium tracking-tight">
          {searching && matched ? (
            <Highlight text={node.name} q={searching} />
          ) : (
            node.name
          )}
        </span>
      )}

      {!editing && childCount > 0 && (
        <span className="shrink-0 rounded-full border border-gray-100 bg-gray-50 px-1.5 font-mono text-[10.5px] text-gray-500">
          {childCount}
        </span>
      )}

      {allowManage && !editing && (
        <span
          className="flex items-center gap-px opacity-0 transition-opacity group-hover/fs-row:opacity-100"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="grid h-[26px] w-[26px] place-items-center rounded-md border-0 bg-transparent text-gray-400 hover:bg-white hover:text-gray-800 hover:shadow-[0_0_0_1px_#e5e7eb]"
            title={t("folder_selector.new_subfolder")}
            onClick={() => onCreateChild(node.id)}
          >
            <FolderPlusIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="grid h-[26px] w-[26px] place-items-center rounded-md border-0 bg-transparent text-gray-400 hover:bg-white hover:text-gray-800 hover:shadow-[0_0_0_1px_#e5e7eb]"
            title={t("folder_selector.rename")}
            onClick={() => onStartRename(node.id)}
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="grid h-[26px] w-[26px] place-items-center rounded-md border-0 bg-transparent text-gray-400 hover:bg-white hover:text-red-600 hover:shadow-[0_0_0_1px_rgba(229,72,77,0.3)]"
            title={t("folder_selector.delete")}
            onClick={() => onDelete(node.id)}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </span>
      )}
    </div>
  );
}

export function ProjectFolderTree({
  scopeTree,
  rows,
  selectedIds,
  focusId,
  spine,
  expanded,
  editingId,
  draft,
  setDraft,
  commitEdit,
  cancelEdit,
  onToggleExpand,
  onRowClick,
  onToggleSelect,
  onCreateChild,
  onStartRename,
  onDelete,
  onMove,
  searching,
  multiSelect = false,
  allowManage = true,
  t,
}) {
  const [drag, setDrag] = useState({ id: null, over: null });

  const rootDrop = (e) => {
    e.preventDefault();
    if (!drag.id || !allowManage) return;
    onMove(drag.id, null, "root-end");
    setDrag({ id: null, over: null });
  };

  return (
    <div
      className="min-h-[140px] flex-1 overflow-auto px-2.5 pb-2.5 pt-1"
      onDragOver={(e) => drag.id && e.preventDefault()}
      onDrop={rootDrop}
    >
      {rows.map((item) => (
        <div key={item.node.id} className="group/fs-row">
          <FolderRow
            item={item}
            scopeTree={scopeTree}
            selected={selectedIds.has(item.node.id)}
            isFocus={focusId === item.node.id}
            inSpine={spine}
            expanded={expanded.has(item.node.id)}
            editing={editingId === item.node.id}
            draft={draft}
            setDraft={setDraft}
            commitEdit={commitEdit}
            cancelEdit={cancelEdit}
            onToggleExpand={onToggleExpand}
            onRowClick={onRowClick}
            onToggleSelect={onToggleSelect}
            onCreateChild={onCreateChild}
            onStartRename={onStartRename}
            onDelete={onDelete}
            drag={drag}
            setDrag={setDrag}
            onMove={onMove}
            matched={item.matched}
            searching={searching}
            multiSelect={multiSelect}
            allowManage={allowManage}
            t={t}
          />
        </div>
      ))}
    </div>
  );
}
